import { useCallback, useRef } from 'react';
import useUiuxStore from 'src/store/useUiuxStore';

export type CommonCodeRequirement = {
	key: string;
	list: unknown[];
	message: string;
};

export type CommonCodeValidateResult = {
	valid: boolean;
	missing: CommonCodeRequirement[];
};

export const useCommonCodeValidate = () => {
	const { setConfirmModalVisible, setConfirmModalProps } = useUiuxStore();
	const shownWarningsRef = useRef(new Set<string>());

	const clearShownWarnings = useCallback(() => {
		shownWarningsRef.current.clear();
	}, []);

	const showCommonCodeWarningModal = useCallback((missing: CommonCodeRequirement[]) => {
		if (missing.length === 0) return;

		const closeModal = () => {
			setConfirmModalVisible(false);
			setConfirmModalProps(undefined);
		};

		setConfirmModalProps({
			title: '설정 오류',
			message: missing.map((item) => item.message).join('\n'),
			confirmText: '확인',
			onConfirm: () => { closeModal(); },
		});
		setConfirmModalVisible(true);
	}, [setConfirmModalProps, setConfirmModalVisible]);

	const validateCommonCodeLists = useCallback((
		requirements: CommonCodeRequirement[],
	): CommonCodeValidateResult => {
		const missing = requirements.filter((requirement) => requirement.list.length === 0);

		if (missing.length === 0) {
			return { valid: true, missing: [] };
		}

		const warningKey = missing.map((item) => item.key).join('|');
		if (!shownWarningsRef.current.has(warningKey)) {
			shownWarningsRef.current.add(warningKey);
			showCommonCodeWarningModal(missing);
		}

		return { valid: false, missing };
	}, [showCommonCodeWarningModal]);

	return {
		validateCommonCodeLists,
		showCommonCodeWarningModal,
		clearShownWarnings,
	};
};
